<template>
  <div class="snake-reports-page">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#427D2D" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <div class="page-content">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">Snake Reports</h1>
          <!-- <p class="page-subtitle">Overview of all reported snake operations and their current status.</p> -->
        </div>
      </div>

      <!-- Status Summary Cards -->
      <div class="stats-row">
        <div
          v-for="(box, i) in statusBoxes"
          :key="i"
          class="stat-card"
          :class="[
            `stat-card--idx-${i}`,
            {
              'stat-card--active': casestatusdata === box.status,
              'stat-card--dull': casestatusdata !== box.status,
            },
          ]"
          @click="selectStatus(box.status)"
        >
          <div class="stat-card__accent" :style="{ background: box.accentColor }"></div>
          <div class="stat-card__body">
            <div class="stat-card__icon" :style="{ background: box.iconBg, boxShadow: box.iconShadow }">
              <v-icon size="22" :color="box.iconColor">{{ box.icon }}</v-icon>
            </div>
            <div class="stat-card__info">
              <div class="stat-card__count" :style="{ color: box.accentColor }">
                {{ stats[box.key] || 0 }}
              </div>
              <div class="stat-card__label">{{ box.name }}</div>
            </div>
          </div>
        </div>
      </div>


      <!-- Filter Inputs -->
      <div v-if="timeperiod || bigsnakes || allsnakes || users" class="filter-inputs">
        <v-layout wrap class="mx-n2">
          <v-flex xs12 sm4 md4 lg3 class="px-2" v-if="timeperiod">
            <v-menu ref="menu" v-model="menu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <span class="field-label">From Date</span>
                <v-text-field
                  v-model="fromdate"
                  label="Select"
                  append-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  :disabled="alldata"
                  class="filter-field"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fromdate"
                color="#de520c"
                :active-picker.sync="activePicker"
                :max="new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substring(0, 10)"
                min="1950-01-01"
                @change="save"
              ></v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex xs12 sm4 md4 lg3 class="px-2" v-if="timeperiod">
            <v-menu ref="menu2" v-model="menu2" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <span class="field-label">To Date</span>
                <v-text-field
                  v-model="todate"
                  label="Select"
                  append-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  :disabled="alldata"
                  class="filter-field"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="todate"
                color="#de520c"
                :active-picker.sync="activePickerto"
                :max="new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substring(0, 10)"
                min="1950-01-01"
                @change="saveto"
              ></v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex xs12 sm4 md4 lg3 class="px-2" v-if="bigsnakes">
            <span class="field-label">Select District</span>
            <v-select
              :items="districts"
              v-model="bigsnakeselect"
              outlined
              multiple
              chips
              deletable-chips
              dense
              hide-details
              :disabled="alldata"
              class="filter-field"
            ></v-select>
          </v-flex>
          <v-flex xs12 sm4 md4 lg3 class="px-2" v-if="allsnakes">
            <span class="field-label">Select Snakes</span>
            <v-select
              :items="snakeitems"
              v-model="allsnakelist"
              multiple
              chips
              deletable-chips
              item-text="name"
              item-value="_id"
              outlined
              dense
              hide-details
              :disabled="alldata"
              class="filter-field"
            ></v-select>
          </v-flex>
          <v-flex xs12 sm4 md4 lg3 class="px-2" v-if="users">
            <span class="field-label">Select Situation</span>
            <v-select
              :items="situationlist"
              v-model="selecteduser"
              outlined
              :disabled="alldata"
              dense
              hide-details
              class="filter-field"
            ></v-select>
          </v-flex>
        </v-layout>
        <div class="filter-submit-row">
          <v-btn color="#de520c" depressed dark @click="updateFiltersAndSearch()" :disabled="alldata" class="submit-btn mr-3">
            Submit
          </v-btn>
        </div>
      </div>

      <!-- Data Table -->
      <div v-if="items && items.length > 0" class="table-section">
        <div class="table-wrapper">
          <v-simple-table class="reports-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left tbl-head">Reported Date</th>
                  <th class="text-left tbl-head">District</th>
                  <th class="text-left tbl-head">Description</th>
                  <th class="text-left tbl-head">Situation</th>
                  <th class="text-left tbl-head">Snake</th>
                  <th class="text-left tbl-head">Status</th>
                  <th class="text-left tbl-head action-col">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in items" :key="index">
                  <td class="cell-nowrap tbl-cell">{{ formatDate(item.reportedAt) }}</td>
                  <td class="cell-nowrap tbl-cell">
                    <span v-if="item && item.rescue && item.rescue.district">{{ item.rescue.district }}</span>
                    <span v-else-if="item && item.reportId && item.reportId.district">{{ item.reportId.district }}</span>
                    <span v-else class="cell-empty">-------</span>
                  </td>
                  <td class="cell-nowrap cell-link tbl-cell" @click="openModal(item.reportId.description)">
                    <span v-if="item && item.reportId.description">{{ item.reportId.description.slice(0, 30) }}..</span>
                    <span v-else class="cell-empty">-------</span>
                  </td>
                  <td class="cell-nowrap tbl-cell">
                    <span v-if="item && item.reportId.situation">{{ item.reportId.situation }}</span>
                    <span v-else class="cell-empty">-------</span>
                  </td>
                  <td class="cell-nowrap tbl-cell">
                    <span v-if="item && ['Rescued', 'Released', 'Approved', 'Closed'].includes(item.status)">
                            <span v-if="item.rescue && item.rescue.snake && item.rescue.snake.name">{{ item.rescue.snake.name }}</span>
                            <span v-else class="cell-empty">-------</span>
                          </span>
                          <span v-else>
                            <span v-if="item.rescue && item.rescue.snake && item.rescue.snake.name">{{ item.rescue.snake.name }}</span>
                            <span v-else-if="item.reportId && item.reportId.snake && item.reportId.snake.name">{{ item.reportId.snake.name }}</span>
                            <span v-else class="cell-empty">-------</span>
                          </span>
                  </td>
                  <td class="cell-nowrap tbl-cell">
                    <span :class="['status-badge', getStatusClass(item.status)]">{{ item.status }}</span>
                  </td>
                  <td class="cell-nowrap tbl-cell">
                    <div class="action-cell">
                      <!-- View -->
                      <button
                        type="button"
                        class="gov-btn gov-btn--view"
                        @click="openviewdetails(item)"
                        title="View Details"
                      >
                        <v-icon size="16" color="white">mdi-eye-outline</v-icon>
                        <span>View</span>
                      </button>

                      <template v-if="mainRole === 'admin'">
                        <!-- Approve -->
                        <button
                          v-if="item.status === 'Released'"
                          type="button"
                          class="gov-btn gov-btn--approve"
                          @click="triggerApprove(item)"
                          title="Approve Operation"
                        >
                          <v-icon size="16" color="white">mdi-check</v-icon>
                          <span>Approve</span>
                        </button>

                        <!-- Delete -->
                        <button
                          v-if="item.status !== 'Fraud'"
                          type="button"
                          class="gov-btn gov-btn--delete"
                          @click="triggerDelete(item)"
                          title="Delete Record"
                        >
                          <v-icon size="16" color="white">mdi-trash-can-outline</v-icon>
                          <span>Delete</span>
                        </button>

                        <!-- Close -->
                        <button
                          v-if="['Pending', 'Assigned', 'Rescued', 'Released'].includes(item.status)"
                          type="button"
                          class="gov-btn gov-btn--close"
                          @click="triggerClose(item)"
                          title="Close Operation"
                        >
                          <v-icon size="16" color="white">mdi-lock-outline</v-icon>
                          <span>Close</span>
                        </button>
                      </template>
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>

        <!-- Pagination Footer -->
        <div class="table-footer">
          <span class="footer-info">
            Showing {{ paginationFrom }} to {{ paginationTo }} of {{ totalRecords }} entries
          </span>
          <v-pagination
            v-model="currentPage"
            :length="Pagelength"
            :total-visible="7"
            color="#de520c"
            class="footer-pagination"
          ></v-pagination>
        </div>
      </div>

      <div v-else class="empty-state">
        <v-icon size="40" color="#cbd5e1">mdi-file-document-outline</v-icon>
        <span class="empty-state-text">No Data Found!</span>
      </div>
    </div>

    <!-- Dialogs -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="heading">Confirmation</v-card-title>
        <v-card-text>Are you sure you want to delete?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined color="#FF1313" light :ripple="false" depressed @click="deleteDialog = false" class="itemValue">
            Cancel
          </v-btn>
          <v-btn tile :color="appColor" light :ripple="false" depressed @click="deleteoperation()" class="itemValue">
            <span style="color: #fff">Submit</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="closeOperationDialog" max-width="500">
      <v-card>
        <v-card-title class="heading">Close Operation</v-card-title>
        <v-card-text>
          <v-layout wrap justify-center>
            <v-flex xs12>
              <v-layout wrap justify-start>
                <v-flex xs12 class="subheading">
                  <span>Remarks</span>
                  <v-text-field outlined dense v-model="closeremarks"></v-text-field>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined color="#FF1313" light :ripple="false" depressed @click="closeOperationDialog = false">
            Cancel
          </v-btn>
          <v-btn tile :color="appColor" light :ripple="false" depressed @click="closeoperation()">
            <span style="color: #fff">Close</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="approveOperationDialog" max-width="500">
      <v-card>
        <v-card-title class="heading">Approve Operation</v-card-title>
        <v-card-text>
          <v-layout wrap justify-center>
            <v-flex xs12>
              <v-layout wrap justify-start>
                <v-flex xs12 class="subheading">
                  <span>Remarks</span>
                  <v-text-field outlined dense v-model="approveremarks"></v-text-field>
                </v-flex>
                <v-flex xs12 class="subheading">
                  <span>ID by Admin</span>
                  <v-text-field outlined dense v-model="idByAdmin"></v-text-field>
                </v-flex>
                <v-flex xs12 class="subheading">
                  <span>Distance (in km)</span>
                  <v-text-field outlined dense v-model="distance"></v-text-field>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined color="#FF1313" light :ripple="false" depressed @click="approveOperationDialog = false">
            Cancel
          </v-btn>
          <v-btn tile :color="appColor" light :ripple="false" depressed @click="approveoperation()">
            <span style="color: #fff">Approve</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectDialog" max-width="500">
      <v-card>
        <v-card-title class="heading">Confirmation</v-card-title>
        <v-card-text>Are you sure you want to reject?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined color="#FF1313" light :ripple="false" depressed @click="rejectDialog = false" class="itemValue">
            Cancel
          </v-btn>
          <v-btn tile :color="appColor" light :ripple="false" depressed @click="rejectoperation()" class="itemValue">
            <span style="color: #fff">Submit</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isModalOpen" max-width="500px">
      <v-card>
        <v-card-title class="dialog-title">Full Description</v-card-title>
        <v-card-text class="label" style="color: black;">
          {{ fullDescription || 'No description available' }}
        </v-card-text>
        <v-card-actions>
          <v-btn color="#FF1313" text @click="closeModal">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
export default {
  data() {
    return {
      stats: {
        pendingCount: 0,
        assignedCount: 0,
        rescuedCount: 0,
        releasedCount: 0,
        approvedCount: 0,
        fraudCount: 0,
        totalCount: 0
      },
      statusBoxes: [
        {
          name: 'Total Cases',
          icon: 'mdi-file-document-multiple-outline',
          iconColor: '#475569',
          iconBg: '#EEF0F4',
          iconShadow: 'none',
          accentColor: '#475569',
          key: 'totalCount',
          status: ''
        },
        {
          name: 'Pending',
          icon: 'mdi-clock-outline',
          iconColor: '#D97706',
          iconBg: '#FEF3E2',
          iconShadow: 'none',
          accentColor: '#d97706',
          key: 'pendingCount',
          status: 'Pending'
        },
        {
          name: 'Assigned',
          icon: 'mdi-account-check',
          iconColor: '#2563eb',
          iconBg: '#EBF1FE',
          iconShadow: 'none',
          accentColor: '#2563eb',
          key: 'assignedCount',
          status: 'Assigned'
        },
        {
          name: 'Rescued',
          icon: 'mdi-snake',
          iconColor: '#16a34a',
          iconBg: '#E6F7EC',
          iconShadow: 'none',
          accentColor: '#16a34a',
          key: 'rescuedCount',
          status: 'Rescued'
        },
        {
          name: 'Released',
          icon: 'mdi-map-marker-check',
          iconColor: '#0891b2',
          iconBg: '#E0F5FA',
          iconShadow: 'none',
          accentColor: '#0891b2',
          key: 'releasedCount',
          status: 'Released'
        },
        {
          name: 'Approved',
          icon: 'mdi-shield-check',
          iconColor: '#65a30d',
          iconBg: '#EEF7E0',
          iconShadow: 'none',
          accentColor: '#65a30d',
          key: 'approvedCount',
          status: 'Approved'
        },
        {
          name: 'Fraud Case',
          icon: 'mdi-alert-octagon',
          iconColor: '#dc2626',
          iconBg: '#FDE8E8',
          iconShadow: 'none',
          accentColor: '#dc2626',
          key: 'fraudCount',
          status: 'Fraud'
        },
      ],
      isModalOpen: false,
      fullDescription: '',
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      count: 10,
      pageSizeOptions: [
        { text: '10 / page', value: 10 },
        { text: '20 / page', value: 20 },
        { text: '50 / page', value: 50 },
      ],
      keyword: "",
      currentPage: 1,
      Pagelength: 0,
      totalRecords: 0,
      userlist: [],
      items: [],
      status: "",
      deleteDialog: false,
      deleteid: "",
      rejectDialog: false,
      rejectid: "",
      alldata: true,
      timeperiod: false,
      bigsnakes: false,
      allsnakes: false,
      users: false,
      activePicker: null,
      fromdate: null,
      menu: false,
      activePickerto: null,
      todate: null,
      menu2: false,
      bigsnakeselect: [],
      allsnakelist: "",
      selecteduser: "",
      bigsnakestypes: [],
      situationlist: [
        "Inside the house",
        "Just outside the building",
        "Natural"
      ],
      casestatus: false,
      casestatusdata: "",
      casestatuslist: [
        "Pending",
        "Assigned",
        "Approved",
        "Rescued",
        "Released",
      ],
      closeOperationDialog: false,
      closeOperationId: "",
      closeremarks: "",
      approveOperationDialog: false,
      approveOperationId: "",
      approveremarks: "",
      idByAdmin: "",
      distance: "",
    };
  },
  beforeMount() {
    if (this.$route.query.status) {
      this.casestatusdata = this.$route.query.status;
    }
    this.$store.dispatch("fetchDistricts");
    this.getStats();
    this.getData();
    this.getList();
  },
  watch: {
    currentPage() {
      this.getData();
    },
    menu(val) {
      val && setTimeout(() => (this.activePicker = "YEAR"));
    },
    menu2(val) {
      val && setTimeout(() => (this.activePickerto = "YEAR"));
    },
    count() {
      this.getData();
    },
    keyword() {
      this.updateFiltersAndSearch();
    },
  },
  computed: {
    mainRole() {
      return this.$store.state.userRole;
    },
    districts() {
      return ["All", ...this.$store.state.districts];
    },
    paginationFrom() {
      if (!this.totalRecords) return 0;
      return (this.currentPage - 1) * this.count + 1;
    },
    paginationTo() {
      if (!this.totalRecords) return 0;
      return Math.min(this.currentPage * this.count, this.totalRecords);
    },
  },
  methods: {
    getStatusClass(status) {
      const map = {
        Approved: 'status-badge--approved',
        Rescued: 'status-badge--rescued',
        Pending: 'status-badge--pending',
        Fraud: 'status-badge--fraud',
        Closed: 'status-badge--closed',
        Assigned: 'status-badge--assigned',
        Released: 'status-badge--released',
      };
      return map[status] || 'status-badge--default';
    },

    activateAllFilter() {
      this.alldata = true;
      this.checkUpdate();
    },

    toggleFilter(filter) {
      this.alldata = false;
      if (filter === 'timeperiod') this.timeperiod = !this.timeperiod;
      if (filter === 'allsnakes') this.allsnakes = !this.allsnakes;
      if (filter === 'bigsnakes') this.bigsnakes = !this.bigsnakes;
    },

    selectStatus(status) {
      this.casestatusdata = status;
      this.currentPage = 1;
      this.getData();
    },

    updateFiltersAndSearch() {
      if (this.currentPage === 1) {
        this.getData();
      } else {
        this.currentPage = 1;
      }
    },

    triggerClose(item) {
      this.closeOperationId = item._id;
      this.closeremarks = "";
      this.closeOperationDialog = true;
    },
    triggerApprove(item) {
      this.approveOperationId = item._id;
      this.approveremarks = "";
      this.idByAdmin = "";
      this.distance = "";
      this.approveOperationDialog = true;
    },
    triggerDelete(item) {
      this.deleteid = item._id;
      this.deleteDialog = true;
    },

    closeoperation() {
      if (!this.closeremarks) {
        this.msg = "Please enter remarks.";
        this.showSnackBar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/operation/closereport",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.closeOperationId,
          remarks: this.closeremarks,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = "Report Closed Successfully.";
            this.showSnackBar = true;
            this.closeOperationDialog = false;
            this.getData();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    approveoperation() {
      if (!this.approveremarks) {
        this.msg = "Please enter remarks.";
        this.showSnackBar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/operation/approve",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.approveOperationId,
          remarks: this.approveremarks,
          idByAdmin: this.idByAdmin,
          distance: this.distance,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = "Approved Successfully.";
            this.showSnackBar = true;
            this.approveOperationDialog = false;
            this.getData();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    getStats() {
      axios({
        method: "GET",
        url: "/snake/report/listCount",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          fromDate: this.fromdate,
          toDate: this.todate,
          snake: this.allsnakelist,
          districts: this.bigsnakeselect,
          situation: this.selecteduser
        }
      }).then(response => {
        if (response.data.status) this.stats = response.data.data;
      });
    },

    openviewdetails(item) {
      let path = "/snakereportsview";
      if (this.$route.path.startsWith("/officers")) {
        path = "/officers/officesnakereportview";
      } else if (this.$route.path.startsWith("/DistrictEmergencyOfficer")) {
        path = "/DistrictEmergencyOfficer/snakereportsview";
      } else if (this.$route.path.startsWith("/medicalOfficers")) {
        path = "/medicalOfficers/snakereportsview";
      }

      this.$router.push({
        path: path,
        query: { id: item._id }
      });
    },
    openModal(description) {
      this.fullDescription = description || 'No description available';
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },

    openrejectDialog(item) {
      this.rejectid = item._id;
      this.rejectDialog = true;
    },
    checkUpdate() {
      if (this.alldata == true) {
        this.timeperiod = false;
        this.bigsnakes = false;
        this.casestatus = false;
        this.allsnakes = false;
        this.users = false;
        this.fromdate = null;
        this.todate = null;
        this.bigsnakeselect = [];
        this.allsnakelist = [];
        this.selecteduser = "";
        this.getData();
      }
    },
    reset() {
      this.timeperiod = false;
      this.bigsnakes = false;
      this.allsnakes = false;
      this.users = false;
      this.alldata = true;
      this.fromdate = null;
      this.todate = null;
      this.bigsnakeselect = [];
      this.allsnakelist = [];
      this.selecteduser = "";
      this.keyword = "";
      this.casestatusdata = "";
      this.updateFiltersAndSearch();
    },
    save(fromdate) {
      this.$refs.menu.save(fromdate);
    },
    saveto(todate) {
      this.$refs.menu2.save(todate);
    },
    getData() {
      this.getStats();
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/operation/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          count: this.count,
          page: this.currentPage,
          fromDate: this.fromdate,
          toDate: this.todate,
          snake: this.allsnakelist,
          districts: this.bigsnakeselect,
          situation: this.selecteduser,
          status: this.casestatusdata,
        }
      })
        .then((response) => {
          if (response.data.status == true) {
            this.items = response.data.data || [];
            this.Pagelength = response.data.pages || 0;
            this.totalRecords = response.data.count || 0;
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.items = [];
            this.totalRecords = 0;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    getList() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/snake/all/",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.snakeitems = response.data.data;
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    deleteoperation() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/operation/delete",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.deleteid,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.msg = "Deleted Successfully.";
            this.showSnackBar = true;
            this.appLoading = false;
            this.deleteDialog = false;
            this.getData();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
            this.deleteDialog = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    rejectoperation() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/operation/removedeleterequest",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.rejectid,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.msg = "Rejected Successfully.";
            this.showSnackBar = true;
            this.appLoading = false;
            this.rejectDialog = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
            this.rejectDialog = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    formatDate(date) {
      if (!date) return "-";
      const [year, month, day] = date.slice(0, 10).split("-");
      return `${day}-${month}-${year}`;
    },
  },
};
</script>

<style scoped>
.snake-reports-page {
  background: #F8F9FC;
  min-height: 100%;
}

.page-content {
  padding: 28px 28px 32px;
}

.page-header {
  padding: 8px 0 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #EDEDF2;
}

.page-title {
  font-family: poppinsmedium, sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.3;
}

.page-subtitle {
  font-family: poppinsmedium, sans-serif;
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

/* Stats Cards */
.stats-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

@media (min-width: 600px) {
  .stats-row {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 1264px) {
  .stats-row {
    grid-template-columns: repeat(7, 1fr);
  }
}

.stat-card {
  position: relative;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #EDEDF2;
  border-left: 3px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

/* Remove the old colored top accent strip — we use left border instead */
.stat-card__accent {
  display: none;
}

.stat-card__body {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 16px 20px;
}

.stat-card__icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  transition: transform 0.2s ease;
}

.stat-card__info {
  min-width: 0;
  flex: 1;
}

.stat-card:hover {
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.10);
  transform: translateY(-1px);
}

.stat-card:hover .stat-card__icon {
  transform: scale(1.05);
}

/* Active card: soft left accent border in the card's own status color */
.stat-card--active {
  border-left-color: var(--card-accent, #de520c);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.09);
}

.stat-card--dull {
  opacity: 0.75;
  filter: saturate(0.60);
  transform: none;
}

.stat-card--dull:hover {
  opacity: 0.92;
  filter: saturate(0.85);
}

/* Per-card: flat white, tinted icon bg via JS — no gradient washes */
.stat-card--idx-0 { --card-accent: #475569; }
.stat-card--idx-1 { --card-accent: #d97706; }
.stat-card--idx-2 { --card-accent: #2563eb; }
.stat-card--idx-3 { --card-accent: #16a34a; }
.stat-card--idx-4 { --card-accent: #0891b2; }
.stat-card--idx-5 { --card-accent: #65a30d; }
.stat-card--idx-6 { --card-accent: #dc2626; }

.stat-card__count {
  font-family: poppinsmedium, sans-serif;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 4px;
  letter-spacing: -0.4px;
}

.stat-card__label {
  font-family: poppinsmedium, sans-serif;
  font-size: 10.5px;
  font-weight: 500;
  color: #8A8FA3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.25;
}


/* Filter Inputs */
.filter-inputs {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  padding: 12px;
  margin-bottom: 12px;
}

.field-label {
  font-family: poppinsmedium, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  display: block;
  margin-bottom: 3px;
}

.filter-field >>> fieldset {
  border-radius: 7px !important;
}

.filter-field >>> .v-input__control {
  min-height: 32px !important;
}

.filter-field >>> input,
.filter-field >>> .v-select__selection {
  font-size: 12px !important;
  font-family: poppinsmedium, sans-serif !important;
}

.filter-submit-row {
  padding-top: 8px;
}

.submit-btn {
  text-transform: none;
  font-family: poppinsregular, sans-serif;
  font-weight: 600;
  font-size: 13px !important;
  border-radius: 8px;
  height: 36px !important;
}

/* Table */
.table-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.table-wrapper {
  overflow-x: auto;
}

.reports-table >>> table {
  border-collapse: collapse;
}

.reports-table >>> thead tr {
  background: #de520c;
}

.tbl-head {
  font-family: poppinsmedium, sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #fff !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  padding: 11px 14px !important;
  white-space: nowrap !important;
  border-bottom: none !important;
  height: 42px !important;
}

.reports-table >>> tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
}

.reports-table >>> tbody tr:hover {
  background: #fafafa;
}

.reports-table >>> tbody tr:nth-child(even) {
  background: #fafbfc;
}

.reports-table >>> tbody tr:nth-child(even):hover {
  background: #f5f5f5;
}

.tbl-cell {
  font-family: poppinsmedium, sans-serif !important;
  font-size: 13px !important;
  color: #334155 !important;
  padding: 10px 14px !important;
  border-bottom: none !important;
  height: 44px !important;
}

.cell-nowrap {
  white-space: nowrap;
}

.cell-empty {
  color: #94a3b8;
}

.cell-link {
  cursor: pointer;
  color: #475569;
}

.cell-link:hover {
  color: #de520c;
}

/* Status Badges */
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-family: poppinsmedium, sans-serif;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1.5;
}

.status-badge--approved,
.status-badge--rescued {
  background: #dcfce7;
  color: #16a34a;
}

.status-badge--pending {
  background: #fef3c7;
  color: #d97706;
}

.status-badge--fraud {
  background: #fee2e2;
  color: #dc2626;
}

.status-badge--closed {
  background: #f1f5f9;
  color: #64748b;
}

.status-badge--assigned {
  background: #dbeafe;
  color: #2563eb;
}

.status-badge--released {
  background: #cffafe;
  color: #0891b2;
}

.status-badge--default {
  background: #f1f5f9;
  color: #475569;
}

/* ── Action Buttons ──────────────────────────────────────────────── */
.action-cell {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 5px;
  min-width: 120px;
}

.gov-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid #94a3b8;
  font-family: poppinsmedium, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;
  white-space: nowrap;
  outline: none;
  background: #fff;
  color: #374151;
  transition: background 0.15s ease;
  letter-spacing: 0.1px;
}

.gov-btn:hover {
  background: #f1f5f9;
  border-color: #64748b;
}

.gov-btn:active {
  background: #e2e8f0;
}

/* All variants inherit the same neutral style — no per-button colour */
.gov-btn--view,
.gov-btn--approve,
.gov-btn--close,
.gov-btn--delete {
  color: #374151;
  border-color: #94a3b8;
}

/* View — Orange */
.gov-btn--view {
  background: #de520c;
  border-color: #de520c;
  color: #fff;
}
.gov-btn--view:hover {
  background: #c44a0a;
  border-color: #c44a0a;
}

/* Approve — Green */
.gov-btn--approve {
  background: #16a34a;
  border-color: #16a34a;
  color: #fff;
}
.gov-btn--approve:hover {
  background: #15803d;
  border-color: #15803d;
}

/* Close — Yellow */
.gov-btn--close {
  background: #ca8a04;
  border-color: #ca8a04;
  color: #fff;
}
.gov-btn--close:hover {
  background: #a16207;
  border-color: #a16207;
}

/* Delete — Red */
.gov-btn--delete {
  background: #dc2626;
  border-color: #dc2626;
  color: #fff;
}
.gov-btn--delete:hover {
  background: #b91c1c;
  border-color: #b91c1c;
}

.action-col {
  min-width: 160px;
}

@media (max-width: 960px) {
  .gov-btn span {
    display: none;
  }
  .gov-btn {
    padding: 5px 7px;
  }
  .action-col {
    min-width: 100px;
  }
}

/* Table Footer */
.table-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  background: #fafbfc;
}

.footer-info {
  font-family: poppinsmedium, sans-serif;
  font-size: 13px;
  color: #64748b;
  white-space: nowrap;
  align-self: center;
}

.footer-pagination {
  flex-shrink: 0;
}

.footer-pagination >>> .v-pagination__item {
  box-shadow: none;
  font-family: poppinsmedium, sans-serif;
  font-size: 13px;
  min-width: 32px;
  height: 32px;
  margin: 0 2px;
}

.footer-pagination >>> .v-pagination__navigation {
  width: 32px;
  height: 32px;
  margin: 0 2px;
}

.footer-pagination >>> .v-pagination__item--active {
  background: #de520c !important;
  color: #fff !important;
}



/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
}

.empty-state-text {
  font-family: poppinsmedium, sans-serif;
  font-size: 13px;
  color: #94a3b8;
  margin-top: 10px;
}

/* Dialog helpers */
.heading {
  font-family: poppinsmedium, sans-serif;
  font-size: 15px;
  font-weight: 700;
}

.dialog-title {
  font-family: poppinsmedium, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #de520c;
  background: #fff7ed;
}

.subheading {
  font-family: poppinsmedium, sans-serif;
  font-size: 12px;
  font-weight: 500;
}
</style>

<style>
/* Non-scoped: force table font size to match the rest of the app */
.tbl-head {
  font-family: poppinsmedium, sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #fff !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  padding: 11px 14px !important;
  white-space: nowrap !important;
  border-bottom: none !important;
  height: 42px !important;
}

.tbl-cell {
  font-family: poppinsmedium, sans-serif !important;
  font-size: 13px !important;
  color: #334155 !important;
  padding: 10px 14px !important;
  border-bottom: none !important;
  height: 44px !important;
}

/* Position Action header above the Delete button */
th.action-col {
  text-align: left !important;
  padding-left: 120px !important;
}
</style>
